// src/components/layout/Navigation.js
import React, { useState, useMemo, useEffect } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { firestoreConnect, isLoaded, useFirestoreConnect } from "react-redux-firebase";
import { compose } from "redux";
import { useTranslation } from "react-i18next";
import styled, { keyframes, css } from "styled-components";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
// isLoaded imported above

// Assets
import logo from "./images/ellipse.png";
import AiCoach from "../ai/AiCoach";
import MastermindNotifications from "../mastermind/MastermindNotifications";
import FinalAutoEvaluationModal from "../auth/FinalAutoEvaluationModal";
import { useFinalEvaluationReminder } from "../../hooks/useFinalEvaluationReminder";
import AdminAiAssistant from "../administration/AdminAiAssistant";
import { BRAND_COLORS } from "../../config/branding"; // Import dynamic branding

// MUI v5 Imports
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Paper,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  InputLabel,
  FormControl,
  Badge,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemAvatar,
  Tab,
  InputBase,
  Stack
} from "@mui/material";

// Icons 
import {
  Edit as EditIcon,
  Dashboard as DashboardIcon,
  DirectionsRun as RunIcon,
  TrendingUp as TrendingUpIcon,
  EventAvailable as EventIcon,
  AutoAwesome as MagicIcon,
  WorkspacePremium as PremiumIcon,
  Person as PersonIcon,
  // Other icons for desktop drawer
  Groups as GroupsIcon,
  Assessment as AssessmentIcon,
  VideoLibrary as VideoLibraryIcon,
  Contacts as ContactsIcon,
  Search as SearchIcon,
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  Description as FileIcon,
  ChevronRight,
  ExpandMore,
  SmartToy as SmartToyIcon,
  AccessTime as AccessTimeIcon,
  DateRange as DateRangeIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Logout as LogoutIcon,
  ManageAccounts as ManageAccountsIcon,
  Mail as MailIcon,
  MarkEmailRead as ReadIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
  Receipt as ReceiptIcon,
  Storefront as StorefrontIcon
} from "@mui/icons-material";

/* ---------- Theme Constants ---------- */
const BRAND = {
  slate: "#2F2F33",
  accent: BRAND_COLORS.blue,        // Dynamic Primary
  ellipseGreen: BRAND_COLORS.accent, // Dynamic Secondary
  deepGreen: "#7A9908",
  mist: "#EAF3D1",
  textMuted: "#6B7280"
};

const DRAWER_WIDTH_EXPANDED = 260;
const DRAWER_WIDTH_COLLAPSED = 72;
const APPBAR_HEIGHT = 80;
const MOBILE_NAV_HEIGHT = 80;

/* ---------- Styled Components ---------- */

const Root = styled(Box)`
  min-height: 100vh;
  background: linear-gradient(180deg, #f6f7fb 0%, #eef1f7 100%);
  display: flex;
  flex-direction: column; /* Changed for mobile stacking */
  @media (min-width: 900px) {
    flex-direction: row;
  }
`;

// Glassmorphism System
const glass = ({ tint = "rgba(255,255,255,0.55)" } = {}) => `
  background: linear-gradient(180deg, rgba(255,255,255,0.78), ${tint});
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.5);
`;

/* ----- Desktop AppBar ----- */
const StyledAppBar = styled(AppBar)`
  ${glass({ tint: "rgba(255,255,255,0.62)" })}
  height: ${APPBAR_HEIGHT}px;
  justify-content: center;
  border-bottom: 0;
  color: ${BRAND.slate};
  z-index: 1200;
  box-shadow: none !important;
  display: none !important; /* Hidden on mobile by default, shown via media query */
  @media (min-width: 900px) {
    display: flex !important;
  }
`;

const TopbarAccent = styled.div`
  position: absolute; left: 0; right: 0; bottom: 0; height: 3px;
  background: linear-gradient(90deg, ${BRAND.ellipseGreen}, ${BRAND.accent});
  opacity: 0.9;
`;

// Mobile Top Bar (Clean Brand)
const MobileTopBar = styled(Box)`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${glass({ tint: "rgba(255,255,255,0.8)" })}
  position: sticky;
  top: 0;
  z-index: 1100;
  @media (min-width: 900px) { display: none; }
`;

/* ----- Mobile Bottom Dock (Apple Style) ----- */
const MobileDock = styled(Box)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${MOBILE_NAV_HEIGHT}px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-top: 1px solid rgba(0,0,0,0.05);
  display: flex;
  justify-content: space-around;
  align-items: center;
  z-index: 1300;
  padding-bottom: env(safe-area-inset-bottom); /* iOS Save Area */
  box-shadow: 0 -10px 40px rgba(0,0,0,0.05);
  
  @media (min-width: 900px) { display: none; }
`;

const DockItem = styled(Button)`
  flex-direction: column;
  min-width: 60px;
  color: ${props => props.$active ? BRAND.accent : "#9CA3AF"} !important;
  gap: 4px;
  text-transform: none !important;
  
  & .label {
    font-size: 10px;
    font-weight: 700;
    opacity: ${props => props.$active ? 1 : 0.8};
  }

  & svg {
    font-size: 24px;
    filter: ${props => props.$active ? `drop-shadow(0 4px 12px ${BRAND.accent}40)` : 'none'};
    transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
    transform: ${props => props.$active ? 'translateY(-2px)' : 'none'};
  }
`;

// Special Center Button for AI Coach
const AiDockButton = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${BRAND.slate}, #000);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${BRAND.ellipseGreen};
  box-shadow: 0 8px 20px rgba(0,0,0,0.25);
  transform: translateY(-24px); /* Floating above dock */
  cursor: pointer;
  border: 4px solid #fff; /* Cutout effect matches bg */
  
  animation: pulseAi 3s infinite;

  @keyframes pulseAi {
    0% { box-shadow: 0 8px 20px rgba(0,0,0,0.25); }
    50% { box-shadow: 0 12px 30px rgba(176, 213, 11, 0.4); }
    100% { box-shadow: 0 8px 20px rgba(0,0,0,0.25); }
  }

  &:active { transform: translateY(-22px) scale(0.95); }
`;

/* ----- Desktop Sidebar (Drawer) ----- */
const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  margin: 4px 8px;
  border-radius: 12px;
  text-decoration: none;
  color: ${props => props.$active ? BRAND.slate : "#4B5563"};
  font-weight: ${props => props.$active ? 900 : 600};
  background: ${props => props.$active ? "rgba(255,255,255,0.85)" : "transparent"};
  border: 1px solid ${props => props.$active ? "rgba(0,0,0,0.05)" : "transparent"};
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255,255,255,0.9);
    transform: translateX(2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }

  svg {
    color: ${props => props.$active ? BRAND.accent : "#9CA3AF"};
  }
`;

/* ---------- Utils & Menu ---------- */
const Initials = (name) => (name || "U").trim().slice(0, 1).toUpperCase();

const buildMenu = (t, profile) => {
  const has = (role) => (profile?.role || "").includes(role);
  const canSeeResources =
    profile?.accessToConfidenceLab === true ||
    has("admin") ||
    (profile?.role || "").includes("ressourcesAdmin") ||
    (profile?.numberHourBuy || 0) > 19;
  const isMastermindMember =
    !!profile?.mastermind ||
    (Array.isArray(profile?.masterminds) && profile.masterminds.length > 0) ||
    profile?.packageType === "MASTERMIND";

  const general = [
    { to: "/", Icon: DashboardIcon, label: t("navigation_dashboard") || "Dashboard" },
    has("admin") && { to: "/my-progress", Icon: TrendingUpIcon, label: "My Passport" },
    isMastermindMember && { to: "/mastermind", Icon: PremiumIcon, label: t("navigation_mastermind") || "Mastermind" },
    { to: "/coaches", Icon: GroupsIcon, label: t("navigation_my_coaches") || "My Coaches" },
    { to: "/meetings", Icon: EventIcon, label: t("navigation_my_meeting") || "Meetings" },
    { to: "/sprint", Icon: RunIcon, label: t("navigation_sprint") || "Sprint" },
    { to: "/academy", Icon: SchoolIcon, label: "Academy" },
    { to: "/confidence", Icon: AssessmentIcon, label: t("navigation_my_confidence") || "Wheel" },
    canSeeResources && { to: "/resources", Icon: VideoLibraryIcon, label: t("navigation_resources") || "Resources" },
    { to: "/attendance", Icon: ContactsIcon, label: t("navigation_my_attendance") || "Attendance" },
  ].filter(Boolean);

  const admin = has("admin") || has("professor") ? [
    { to: "/mastermind/coach", Icon: PremiumIcon, label: "Mastermind — Coach Console" },
    { to: "/administration", Icon: AdminIcon, label: "Global Admin (Coaches & Pay)" }, // Renamed for clarity
    { to: "/learningLogs", Icon: SearchIcon, label: "Member Search" },
    { to: "/my-timesheet", Icon: AccessTimeIcon, label: "My Personal Timesheet" },
    { to: "/sprintAdministration", Icon: RunIcon, label: "Sprints Management" },
    { to: "/companiesAdministration", Icon: BusinessIcon, label: "Company Portal" },
    { to: "/sprintPlanning", Icon: DateRangeIcon, label: "Sprint Planning" },
    { to: "/sprintattendance", Icon: ContactsIcon, label: "Sprint Attendance" },
    { to: "/ai-config", Icon: SmartToyIcon, label: "AI Coach Config" },
    { to: "/lead-config", Icon: SmartToyIcon, label: "Leads & Quiz Config" },
    { to: "/administration?tab=branding", Icon: EditIcon, label: "Design & Branding" },
    { to: "/administration?tab=masterminds", Icon: GroupsIcon, label: "Masterminds" },
    { to: "/administration?tab=feedback", Icon: AssessmentIcon, label: "Surveys & Feedbacks" }, // [NEW] Link to Feedback Tab
    ...(has("admin") || has("legal") ? [{ to: "/contractGenerator", Icon: FileIcon, label: "Contract Generator" }] : []),
    // CRM for Admins Only
    ...(has("admin") ? [{ to: "/crm", Icon: ContactsIcon, label: "CRM Assistant (Admin)" }] : []),
    ...(has("admin") ? [{ to: "/quickbooks-assistant", Icon: ReceiptIcon, label: "QuickBooks Assistant" }] : []),
    ...(has("admin") ? [{ to: "/admin/statistics", Icon: AssessmentIcon, label: "Member Health Dashboard" }] : []),
    ...(has("admin") ? [{ to: "/admin/sales-stats", Icon: AssessmentIcon, label: "Sales Statistics (QB)" }] : []),
    ...(has("admin") ? [{ to: "/admin/intelligence", Icon: MagicIcon, label: "AI Client Intelligence" }] : []),
  ] : [];

  // Rôle Finance : accès limité à la facturation/recouvrement (pas le reste de l'admin).
  // Les admins voient déjà ces outils via le menu Admin ; ce groupe est pour les finance-only.
  const finance = (has("finance") && !has("admin") && !has("professor")) ? [
    { to: "/quickbooks-assistant", Icon: ReceiptIcon, label: "QuickBooks Assistant" },
    { to: "/admin/sales-stats", Icon: AssessmentIcon, label: "Sales Statistics (QB)" },
  ] : [];

  return [
    { group: "General", items: general },
    admin.length ? { group: "Admin", items: admin } : null,
    finance.length ? { group: "Finance", items: finance } : null,
  ].filter(Boolean);
};

/* ---------- Apple.com Style Navbar (Light Glass) ---------- */
const AppleNavBar = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px; /* "Even Better" Pro Height */
  background: rgba(255, 255, 255, 0.72); /* More transparent for visibility */
  backdrop-filter: saturate(180%) blur(20px); /* Slightly softer blur */
  -webkit-backdrop-filter: saturate(180%) blur(25px);
  border-bottom: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 4px 30px rgba(0,0,0,0.03); /* Softer, deeper shadow */
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 32px;
  transition: all 0.3s ease;
  @media (max-width: 900px) { display: none; }
`;

const NavContainer = styled(Box)`
  width: 100%;
  max-width: 1440px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
`;

const AppleLink = styled(Link)`
  font-size: 14px; /* Increased from 13px */
  font-weight: 500;
  color: ${BRAND.slate};
  opacity: 0.7;
  text-decoration: none;
  transition: all 0.2s ease;
  padding: 8px 16px; /* More breathable pill */
  margin: 0 6px;
  border-radius: 12px;
  
  &:hover {
    opacity: 1;
    background: rgba(0,0,0,0.05);
    color: #000;
    transform: translateY(-1px); /* Subtle Lift */
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }

  /* Active State */
  ${props => props.$active && css`
    opacity: 1;
    font-weight: 600;
    color: ${BRAND.accent};
    background: rgba(0, 186, 238, 0.08);
  `}
`;

const AppleButton = styled(Button)`
  font-size: 14px; /* Increased from 13px */
  font-weight: 500;
  color: ${BRAND.slate} !important;
  text-transform: none;
  opacity: 0.7;
  min-width: 0;
  transition: all 0.2s ease;
  padding: 6px 12px;
  margin: 0 4px;
  border-radius: 8px;
  font-family: inherit;
  
  &:hover { opacity: 1; background: rgba(0,0,0,0.04); color: #000 !important; }
`;

/* ---------- Apple-style Mega Menu (Full Width) ---------- */
const MegaMenuOverlay = styled(Box)`
  position: fixed;
  top: 48px;
  left: 0;
  right: 0;
  height: auto;
  min-height: 120px;
  padding: 32px 0 48px 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
  border-bottom: 1px solid rgba(0,0,0,0.05);
  z-index: 1150;
  display: flex;
  justify-content: center;
  box-shadow: 0 20px 40px rgba(0,0,0,0.05);
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$open ? '0' : '-10px'});
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const MegaGrid = styled(Box)`
  width: 100%;
  max-width: 1024px;
  display: grid;
  grid-template-columns: repeat(5, 1fr); /* 5 Columns for layout */
  gap: 24px;
  padding: 0 24px;
`;

const MegaLink = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-decoration: none;
  
  & .label {
    font-size: 13px;
    font-weight: 600;
    color: ${BRAND.slate};
    transition: color 0.1s;
  }
  
  & .sub {
    font-size: 11px;
    color: ${BRAND.textMuted};
    transition: color 0.1s;
  }

  &:hover .label { color: ${BRAND.accent}; }
  &:hover .sub { color: ${BRAND.slate}; }
`;

const MenuBackdrop = styled(Box)`
  position: fixed;
  top: 48px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.1);
  backdrop-filter: blur(2px);
  z-index: 1140;
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transition: all 0.3s;
`;

/* ---------- Component: Navigation ---------- */
function Navigation(props) {
  const { auth, profile } = props;
  const { t, i18n } = useTranslation();
  const isFr = i18n.language !== 'en';
  const location = useLocation();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [expanded, setExpanded] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [currentAvatar, setCurrentAvatar] = useState(null); // Optimistic Avatar State
  const [adminOpen, setAdminOpen] = useState(false); // Mega Menu State
  const [coachOpen, setCoachOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState(null); // New for More Dropdown
  const [smsOpen, setSmsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editOpen, setEditOpen] = useState(false); // New state for Edit Modal
  const [adminAssistantOpen, setAdminAssistantOpen] = useState(false); // Admin AI Assistant State
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [newPortalOpen, setNewPortalOpen] = useState(false);

  // --- Messaging System State ---
  const [inboxOpen, setInboxOpen] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState(null); // ID of the other user
  const [replyText, setReplyText] = useState("");

  // --- Final Auto-Evaluation Reminder ---
  const { modalOpen: finalEvalModalOpen, setModalOpen: setFinalEvalModalOpen } = useFinalEvaluationReminder(auth, profile);

  // --- Dynamic Thread Users Query (Moved here to stabilize) ---
  const userIds = useMemo(() => {
    const ids = new Set();
    const messages = [...(props.receivedMessages || []), ...(props.sentMessages || [])];
    messages.forEach(m => {
      if (m.fromId && m.fromId !== props.auth.uid) ids.add(m.fromId);
      if (m.toId && m.toId !== props.auth.uid) ids.add(m.toId);
    });
    return Array.from(ids).slice(0, 10).sort(); // Sort stability
  }, [props.receivedMessages, props.sentMessages, props.auth.uid]);

  useFirestoreConnect(useMemo(() => {
    if (userIds.length === 0) return [];
    return [{
      collection: 'users',
      where: [firebase.firestore.FieldPath.documentId(), 'in', userIds],
      storeAs: 'threadUsers'
    }];
  }, [userIds.join(',')])); // Stabilize dependency string (array ref changes)

  const receivedMessages = props.receivedMessages || [];
  const sentMessages = props.sentMessages || [];
  const unreadCount = receivedMessages.filter(m => !m.read).length;

  // Process threads
  const threads = useMemo(() => {
    const all = [...receivedMessages, ...sentMessages];
    console.log("DEBUG WEB: AuthID:", props.auth.uid, "Rec:", receivedMessages.length, "Sent:", sentMessages.length);
    if (receivedMessages.length > 0) console.log("DEBUG WEB: First Rec:", receivedMessages[0]);
    // Group by other user ID
    const groups = {};
    all.forEach(msg => {
      const isMe = msg.fromId === props.auth.uid;
      const otherId = isMe ? msg.toId : msg.fromId;
      const otherName = isMe ? msg.toName : msg.fromName;

      let avatarUrl = null;
      if (props.threadUsers && props.threadUsers[otherId]) {
        avatarUrl = props.threadUsers[otherId].avatarUrl || props.threadUsers[otherId].photoURL;
      }
      if (!avatarUrl && props.professors) {
        const prof = Object.values(props.professors).find(p => p && (p.id === otherId || p.uid === otherId));
        if (prof) avatarUrl = prof.pictureUrl;
      }

      if (!groups[otherId]) {
        groups[otherId] = { id: otherId, name: otherName, messages: [], lastMsg: null, avatar: avatarUrl, unreadCount: 0 };
      }
      groups[otherId].messages.push(msg);
      if (!isMe && !msg.read) {
        groups[otherId].unreadCount = (groups[otherId].unreadCount || 0) + 1;
      }
    });
    // Helper to get time safely
    const getTime = (t) => {
      if (!t) return 0;
      if (t.seconds) return t.seconds;
      if (t.getTime) return t.getTime() / 1000;
      if (t instanceof Date) return t.getTime() / 1000;
      return 0;
    };

    // Sort messages in each thread
    Object.values(groups).forEach(g => {
      g.messages.sort((a, b) => getTime(a.createdAt) - getTime(b.createdAt));
      g.lastMsg = g.messages[g.messages.length - 1];
    });
    // Return sorted groups (most recent first)
    return Object.values(groups).sort((a, b) => getTime(b.lastMsg?.createdAt) - getTime(a.lastMsg?.createdAt));
  }, [receivedMessages, sentMessages, props.auth.uid, props.professors, props.threadUsers]);

  const activeThread = threads.find(t => t.id === selectedThreadId);

  const handleMarkRead = async (msgId) => {
    await firebase.firestore().collection('messages').doc(msgId).update({ read: true });
  };

  // Mark messages as read only when inbox is open AND thread is active
  useEffect(() => {
    if (!inboxOpen || !activeThread) return;
    const unread = activeThread.messages.filter(m => m.fromId !== props.auth.uid && !m.read);
    unread.forEach(m => handleMarkRead(m.id));
  }, [inboxOpen, selectedThreadId, activeThread?.messages.length]);

  const handleDeleteThread = async (thread) => {
    if (window.confirm(`Delete entire conversation with ${thread.name}? This cannot be undone.`)) {
      const batch = firebase.firestore().batch();
      thread.messages.forEach(msg => {
        const ref = firebase.firestore().collection("messages").doc(msg.id);
        batch.delete(ref);
      });
      await batch.commit();
      setSelectedThreadId(null);
    }
  };

  const handleDeleteMessage = async (msgId) => {
    if (window.confirm("Delete this message?")) {
      await firebase.firestore().collection("messages").doc(msgId).delete();
    }
  };

  const handleUpdateProfile = async (field, value) => {
    try {
      // 1. Update User Profile
      await firebase.firestore().collection("users").doc(props.auth.uid).update({ [field]: value });

      // 2. Check and Update Professor Profile if exists
      if (props.professors) {
        const myProfDoc = Object.values(props.professors).find(p => p && (p.uid === props.auth.uid || (p.email && p.email === props.auth.email)));
        if (myProfDoc) {
          const updates = { [field]: value };
          if (field === 'firstname' || field === 'lastname') {
            const fname = field === 'firstname' ? value : (props.profile.firstname || '');
            const lname = field === 'lastname' ? value : (props.profile.lastname || '');
            updates.name = `${fname} ${lname}`.trim();
          }
          await firebase.firestore().collection("professor").doc(myProfDoc.id).update(updates);
        }
      }
    } catch (e) {
      console.error("Error updating profile:", e);
    }
  };

  const handleLanguageChange = async (lng) => {
    i18n.changeLanguage(lng);
    if (auth.uid) {
      try {
        await firebase.firestore().collection("users").doc(auth.uid).update({
          language: lng
        });
        console.log("Language updated in Firestore:", lng);
      } catch (e) {
        console.error("Error persisting language:", e);
      }
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !activeThread) return;
    try {
      await firebase.firestore().collection("messages").add({
        fromId: props.auth.uid,
        fromName: props.profile.firstname || "User",
        toId: activeThread.id,
        toName: activeThread.name,
        content: replyText.trim(),
        createdAt: new Date(),
        read: false
      });
      setReplyText("");
    } catch (e) {
      console.error(e);
    }
  };



  const LEVELS = [
    { value: 'A1', label: 'Beginner (A1)' },
    { value: 'A2', label: 'Elementary (A2)' },
    { value: 'B1', label: 'Intermediate (B1)' },
    { value: 'B2', label: 'Upper Intermediate (B2)' },
    { value: 'C1', label: 'Advanced (C1)' }
  ];

  // Sync avatar from DB when profile loads
  useEffect(() => {
    // Logic Refinement:
    // If profile is loaded, it is the source of truth.
    if (profile && !profile.isEmpty && isLoaded(profile)) {

      // PRIORITY 1: Explicit 'avatarUrl' (Our main manual field)
      // We check for undefined so that an empty string "" (deleted) is RESPECTED and doesn't fall through.
      if (profile.avatarUrl !== undefined && profile.avatarUrl !== null) {
        setCurrentAvatar(profile.avatarUrl || null);
        return;
      }

      // PRIORITY 2: Legacy fields
      // If code reaches here, avatarUrl was undefined/null.
      const legacySource = profile.avatar || profile.photoURL || profile.image;
      setCurrentAvatar(legacySource || null);

    } else {
      // Fallback to Auth only if profile isn't ready
      // Note: This might flash the Google photo briefly before profile loads 
      // and overrides it with "" (Initials), which is acceptable.
      setCurrentAvatar(auth?.photoURL || null);
    }
  }, [profile, auth]);


  const has = (role) => (profile?.role || "").includes(role);
  const isTrial = profile?.plan === 'TRIAL';
  const trialExpired = isTrial && profile?.bootCampEndDate
    ? new Date(profile.bootCampEndDate + 'T23:59:59') < new Date()
    : false;

  const canSeeAcademy =
    profile?.accessToAcademy === true ||
    has("admin");
    
  const canSeeResourcesLocal =
    profile?.accessToConfidenceLab === true ||
    has("admin") ||
    (profile?.role || "").includes("ressourcesAdmin") ||
    (profile?.numberHourBuy || 0) > 19;

  // Handlers
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !auth.uid) return;

    // CHECK FOR HEIC (Apple Format not supported on web)
    if (file.name.toLowerCase().endsWith('.heic') || file.type === 'image/heic') {
      alert("Apple HEIC format is not supported by browsers. Please use a JPG or PNG image (or a screenshot).");
      return;
    }

    try {
      setUploading(true);
      const storageRef = firebase.storage().ref();
      const fileRef = storageRef.child(`avatars/${auth.uid}/${Date.now()}_${file.name}`);

      await fileRef.put(file);
      const url = await fileRef.getDownloadURL();

      // IMMEDIATE OPTIMISTIC UPDATE
      setCurrentAvatar(url);

      // Update Firestore Users
      await firebase.firestore().collection("users").doc(auth.uid).set({
        avatarUrl: url,
        photoURL: url,
        avatar: url
      }, { merge: true });

      // SYNC PROFESSOR
      if (props.professors) {
        const myProf = Object.values(props.professors).find(p => p && (p.uid === auth.uid || (p.email && p.email === auth.email)));
        if (myProf) {
          await firebase.firestore().collection("professor").doc(myProf.id).update({
            pictureUrl: url
          });
        }
      }

      // Update Auth Profile
      if (firebase.auth().currentUser) {
        await firebase.auth().currentUser.updateProfile({ photoURL: url });
      }

    } catch (error) {
      console.error("Error uploading avatar:", error);
      alert("Error: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAvatar = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!auth.uid) return;

    try {
      setUploading(true);

      // IMMEDIATE OPTIMISTIC UPDATE
      setCurrentAvatar(null);

      // DB Update (Set to empty string to EXPLICITLY override Auth provider/Google photo)
      await firebase.firestore().collection("users").doc(auth.uid).update({
        avatarUrl: "",
        photoURL: "",
        avatar: "",
        image: ""
      });

      // Auth Update - Force null and reload to prevent stale data
      if (firebase.auth().currentUser) {
        await firebase.auth().currentUser.updateProfile({ photoURL: null });
        await firebase.auth().currentUser.reload();
      }

    } catch (error) {
      console.error("Error removing avatar:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleTogglePrivacy = async (e) => {
    if (!auth.uid) return;
    const isChecked = e.target.checked;
    const updates = { sharePhoto: isChecked };

    // If turning ON sharing and no custom avatar exists but Google photo does, sync it to Firestore
    if (isChecked && !profile?.avatarUrl && auth?.photoURL) {
      updates.avatarUrl = auth.photoURL;
    }

    await firebase.firestore().collection("users").doc(auth.uid).update(updates);
  };


  const handleLogout = async () => {
    await firebase.auth().signOut();
    history.push("/signin");
  };

  const menuGroups = useMemo(() => buildMenu(t, profile), [t, profile]);
  const showChrome = !["/signin", "/signup"].includes(location.pathname);

  // Common Props
  const dockItems = [
    { to: "/", label: t("navigation_home") || "Home", icon: DashboardIcon },
    !isTrial && { to: "/sprint", label: t("navigation_sprint") || "Sprint", icon: RunIcon },
    !isTrial && { action: "ai", label: t("navigation_ai_coach") || "AI Coach", icon: MagicIcon }, // Special center
    !isTrial && { to: "/coaches", label: t("navigation_my_coaches") || "Coaches", icon: GroupsIcon },
    { action: "profile", label: t("navigation_profile") || "Profile", icon: PersonIcon }
  ].filter(Boolean);

  const handleMobileNav = (item) => {
    if (item.action === 'ai') setCoachOpen(true);
    else if (item.action === 'profile') setAnchorElUser(true); // Simplified profile trigger or implement drawer
    else history.push(item.to);
  };



  // New portal welcome — show once, stored in Firestore
  // Désactivé temporairement — retirer le return pour réactiver
  useEffect(() => {
    return;
    if (!profile || profile.isEmpty || !isLoaded(profile)) return;
    if (!auth?.uid) return;
    if (profile.hasSeenNewPortal2025 === true) return;
    setNewPortalOpen(true);
  }, [profile, auth?.uid]);

  const handleDismissNewPortal = async () => {
    setNewPortalOpen(false);
    if (auth?.uid) {
      await firebase.firestore().collection("users").doc(auth.uid).update({ hasSeenNewPortal2025: true });
    }
  };

  // Click away logic
  useEffect(() => {
    const close = () => setAdminOpen(false);
    if (adminOpen) window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [adminOpen]);

  return (
    <Root>
      {showChrome && (
        <>
          {/* 1. DESKTOP: APPLE GLOBAL NAV */}
          <AppleNavBar>
            <NavContainer>
              {/* Logo */}
              <Link to="/">
                <img src={logo} alt="Ellipse" style={{ height: 28, opacity: 1, transition: 'transform 0.2s' }} /> {/* Bigger Logo */}
              </Link>

              {/* Center Links */}
              <Box display="flex" alignItems="center">
                {/* Manually mapping "General" items for clean control */}
                <AppleLink to="/" $active={location.pathname === '/'}>{t("navigation_dashboard") || "Dashboard"}</AppleLink>
                {trialExpired ? (
                  <Tooltip title={isFr ? "Ton trial est expiré — passe au plan complet" : "Your trial has expired — upgrade to continue"} arrow placement="bottom">
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, opacity: 0.35, cursor: 'not-allowed', fontSize: '0.875rem', fontWeight: 500 }}>
                      <LockIcon sx={{ fontSize: 12 }} />{t("navigation_sprint") || "Sprint"}
                    </Box>
                  </Tooltip>
                ) : (
                  <AppleLink to="/sprint" $active={location.pathname === '/sprint'}>{t("navigation_sprint") || "Sprint"}</AppleLink>
                )}
                {has("admin") && (
                  <AppleLink to="/my-progress" $active={location.pathname === '/my-progress'}>{t("navigation_my_passport") || "My Passport"}</AppleLink>
                )}



                {isTrial ? (
                  <Tooltip title={isFr ? "Disponible avec un plan complet" : "Available with a full plan"} arrow placement="bottom">
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, opacity: 0.35, cursor: 'not-allowed', fontSize: '0.875rem', fontWeight: 500 }}>
                      <LockIcon sx={{ fontSize: 12 }} />{t("navigation_schedule") || "Schedule"}
                    </Box>
                  </Tooltip>
                ) : (
                  <AppleLink to="/meetings" $active={location.pathname === '/meetings'}>{t("navigation_schedule") || "Schedule"}</AppleLink>
                )}

                {isTrial ? (
                  <Tooltip title={isFr ? "Disponible avec un plan complet" : "Available with a full plan"} arrow placement="bottom">
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, opacity: 0.35, cursor: 'not-allowed', fontSize: '0.875rem', fontWeight: 500 }}>
                      <LockIcon sx={{ fontSize: 12 }} />{t("navigation_coaches") || "Coaches"}
                    </Box>
                  </Tooltip>
                ) : (
                  <AppleLink to="/coaches" $active={location.pathname === '/coaches'}>{t("navigation_coaches") || "Coaches"}</AppleLink>
                )}


                {/* More Dropdown */}
                {!isTrial && (
                  <AppleButton
                    onClick={(e) => setMoreAnchor(e.currentTarget)}
                    sx={{
                      fontWeight: ['/confidence', '/arcade', '/attendance'].includes(location.pathname) ? 700 : 500,
                      color: ['/confidence', '/arcade', '/attendance'].includes(location.pathname) ? BRAND.accent : undefined
                    }}
                    endIcon={<ExpandMore fontSize="small" />}
                  >
                    {t("navigation_tools") || "Tools"}
                  </AppleButton>
                )}



                {/* Admin Toggle */}
                {(has("admin") || has("professor") || has("finance")) && (
                  <AppleButton
                    onClick={(e) => { e.stopPropagation(); setAdminOpen(!adminOpen); }}
                    sx={{
                      color: adminOpen ? `${BRAND.accent} !important` : undefined,
                      fontWeight: adminOpen ? 600 : 500,
                      bgcolor: adminOpen ? 'rgba(0, 186, 238, 0.08)' : 'transparent'
                    }}
                  >
                    {t("navigation_administration") || "Admin"}
                  </AppleButton>
                )}
              </Box>




              {/* Right: Profile & Notifications */}
              <Box display="flex" alignItems="center" gap={1}>
                {/* Mastermind notifications (global) — for Mastermind members */}
                {(!!profile?.mastermind || (Array.isArray(profile?.masterminds) && profile.masterminds.length > 0) || profile?.packageType === "MASTERMIND") && (
                  <MastermindNotifications auth={props.auth} isFr={i18n.language?.toLowerCase().startsWith("fr")} color={BRAND.slate} />
                )}
                {/* NOTIFICATION BADGE */}
                <IconButton size="small" onClick={() => setInboxOpen(true)}>
                  <Badge badgeContent={unreadCount} color="error">
                    <MailIcon sx={{ color: BRAND.slate, opacity: 0.8 }} />
                  </Badge>
                </IconButton>

                {!has("admin") && (
                  <IconButton size="small" onClick={() => setCoachOpen(true)}>
                    <MagicIcon sx={{ fontSize: 20, color: BRAND.slate, opacity: 0.8 }} />
                  </IconButton>
                )}
                <IconButton size="small" onClick={handleOpenUserMenu} sx={{ ml: 1 }}>
                  <Avatar
                    src={currentAvatar}
                    imgProps={{ referrerPolicy: "no-referrer" }}
                    sx={{ width: 24, height: 24, fontSize: 10, bgcolor: 'transparent', color: BRAND.slate, border: '1px solid #ddd' }}
                  >
                    {Initials(profile?.firstname)}
                  </Avatar>
                </IconButton>
              </Box>
            </NavContainer>
          </AppleNavBar>

          {/* 2. MOBILE TOP BAR */}
          <MobileTopBar>
            <img src={logo} alt="Ellipse" style={{ height: 32 }} />
          </MobileTopBar>

          {/* 3. MAIN CONTENT */}
          <Box component="main" sx={{
            flexGrow: 1,
            // Desktop: Top padding for Apple Bar (48px) + spacing
            pt: { xs: '60px', md: '80px' },
            // Reset left padding to 0
            pl: 0,
            pb: { xs: `${MOBILE_NAV_HEIGHT}px`, md: 3 },
            px: { xs: 2, md: 3 },
            minHeight: '100vh',
            maxWidth: '1440px', // Apple Style Standard (Focused)
            mx: 'auto',
            width: '100%'
          }}>
            {props.children}
          </Box>

          {/* 4. MOBILE BOTTOM DOCK (Visible on Mobile Only) */}
          <MobileDock>
            {dockItems.map((item, index) => {
              const isActive = location.pathname === item.to;

              if (item.action === 'ai') {
                if (has("admin")) return null; // Hide Student Coach for Admins
                return (
                  <AiDockButton key={index} onClick={() => setCoachOpen(true)}>
                    <MagicIcon />
                  </AiDockButton>
                );
              }

              return (
                <DockItem
                  key={index}
                  $active={isActive}
                  onClick={() => item.action === 'profile' ? handleOpenUserMenu({ currentTarget: document.body }) : history.push(item.to)}
                >
                  <item.icon />
                  <span className="label">{item.label}</span>
                </DockItem>
              );
            })}
          </MobileDock>

          {/* --- APPLE MEGA MENU (FULL WIDTH) --- */}
          {/* Backdrop for explicit click-away */}
          <MenuBackdrop $open={adminOpen} onClick={() => setAdminOpen(false)} />

          <MegaMenuOverlay $open={adminOpen} onClick={(e) => e.stopPropagation()}>
            <Box display="flex" justifyContent="center" width="100%" maxWidth="1200px" px={4} gap={4}>
              {[
                {
                  title: isFr ? "Vue d'ensemble" : "Overview",
                  items: [
                    { to: "/admin/scoreboard",   label: isFr ? "Tableau de bord"       : "The Score Card",       sub: isFr ? "Mission Control"       : "Mission Control",     req: "admin" },
                    { to: "/admin/site-map",     label: isFr ? "Plan du site (Index)"  : "Site Map (Index)",     sub: isFr ? "Tout l'admin + landings" : "All admin + landings", req: "admin" },
                    { to: "/admin/statistics",   label: isFr ? "Santé des membres"     : "Member Health Dashboard", sub: isFr ? "Rétention & engagement" : "Retention & engagement", req: "admin" },
                    { to: "/admin/sales-stats",  label: isFr ? "Ventes (QB)"           : "Sales Statistics (QB)", sub: isFr ? "Données live de QuickBooks" : "Live from QuickBooks",      req: "finance" },
                    { to: "/admin/intelligence", label: isFr ? "Intelligence client"   : "Client Intelligence",  sub: isFr ? "Analyse IA"            : "AI Analysis",         req: "admin" },
                    { to: "/admin/landing-pages",label: isFr ? "Pages d'atterrissage"  : "Landing Pages",        sub: isFr ? "Événements"            : "Events",              req: "admin" },
                    { to: "/administration?tab=surveys", label: isFr ? "Sondages"      : "Surveys",              sub: isFr ? "Résultats globaux"     : "Global Results",      req: "admin" },
                    { to: "/crm",                label: "CRM",                                                    sub: "Pipedrive A.I.",                                        req: "admin" },
                  ]
                },
                {
                  title: isFr ? "Marketing" : "Marketing",
                  items: [
                    { to: "/admin/marketing",      label: isFr ? "Dashboard Marketing"  : "Marketing Dashboard",  sub: isFr ? "CAC · LTV · ROAS"      : "CAC · LTV · ROAS",    req: "admin" },
                    { to: "/admin/integrations",   label: isFr ? "Intégrations"         : "Integrations",         sub: isFr ? "50+ connecteurs"       : "50+ connectors",      req: "admin" },
                    { to: "/admin/add-lead",        label: isFr ? "Ajouter un lead"      : "Add Lead",             sub: isFr ? "Funnel Sprint B2C"     : "Sprint B2C Funnel",   req: "admin" },
                    { to: "/admin/linkedin-prospects", label: isFr ? "Prospects LinkedIn" : "LinkedIn Prospects",  sub: isFr ? "Extension Ellipse"     : "Ellipse extension",   req: "admin" },
                    { to: "/admin/scoring-config",  label: isFr ? "Config scoring"        : "Scoring Config",       sub: isFr ? "Lead Score"             : "Lead Score",          req: "admin" },
                    { to: "/admin/warming-leads",   label: isFr ? "Leads tiedes"          : "Warming Leads",        sub: isFr ? "Score 30-59"            : "Score 30-59",         req: "admin" },
                    { to: "/admin/ghl-migration",   label: isFr ? "Transfert GHL"         : "GHL Transfer",         sub: isFr ? "Pipedrive → GoHighLevel" : "Pipedrive → GoHighLevel", req: "admin" },
                  ]
                },
                {
                  title: isFr ? "Gestion" : "Management",
                  items: [
                    { to: "/administration?tab=timesheets",  label: isFr ? "Coachs & Paiement"  : "Coaches & Pay",     sub: isFr ? "Équipe & feuilles"   : "Team & Timesheets",  req: "admin" },
                    { to: "/companiesAdministration",        label: isFr ? "Entreprises"         : "Companies",         sub: isFr ? "Partenaires"         : "Partners",           req: "admin" },
                    { to: "/administration?tab=masterminds", label: "Masterminds",                                      sub: isFr ? "Groupes"             : "Groups",             req: "admin" },
                    { to: "/mastermind/coach",               label: isFr ? "Mastermind — Console Coach" : "Mastermind — Coach Console", sub: isFr ? "Présences & séances" : "Attendance & sessions", req: "any" },
                    { to: "/contractGenerator",              label: isFr ? "Contrats"            : "Contracts",         sub: isFr ? "Générer"             : "Generate",           req: "legal" },
                    { to: "/administration?tab=contracts",   label: isFr ? "Config contrats"    : "Contract Config",   sub: isFr ? "Prix & modèles"      : "Pricing & Templates",req: "admin" },
                    { to: "/quickbooks-assistant",           label: "QuickBooks",                                       sub: isFr ? "Factures impayées"   : "Unpaid Invoices",    req: "finance" },
                    { to: "/admin/boutique-config",          label: isFr ? "Config Boutique"     : "Boutique Config",   sub: isFr ? "Gérer prix & offres"  : "Manage offers & pricing", req: "admin" },
                  ]
                },
                {
                  title: isFr ? "Opérations" : "Operations",
                  items: [
                    { to: "/sprintAdministration", label: isFr ? "Admin Sprints"      : "Sprint Admin",       sub: isFr ? "Gérer les cycles"     : "Manage Cycles",   req: "admin" },
                    { to: "/sprintPlanning",       label: isFr ? "Planification"      : "Planning",           sub: isFr ? "Calendrier"           : "Calendar",        req: "admin" },
                    { to: "/sprintattendance",     label: isFr ? "Présences Sprint"   : "Sprint Attendance",  sub: isFr ? "Journaux de cours"    : "Class Logs",      req: "any" },
                    { to: "/learningLogs",         label: isFr ? "Recherche membres"  : "Member Search",      sub: isFr ? "Données & journaux"   : "Logs & Data",     req: "any" },
                    { to: "/my-timesheet",         label: isFr ? "Ma feuille"         : "My Timesheet",       sub: isFr ? "Heures & paiement"    : "Hours & Pay",     req: "any" },
                    { to: "/administration?tab=evaluations", label: isFr ? "Évaluations" : "Evaluations",     sub: isFr ? "Types & config"       : "Types & Config",  req: "admin" },
                  ]
                },
                {
                  title: isFr ? "Système" : "System",
                  items: [
                    { to: "/administration?tab=ai",            label: isFr ? "IA & Leads"       : "AI & Leads",       sub: isFr ? "Config & prompts"   : "Config & Prompts",  req: "admin" },
                    { to: "/email-preview",                    label: isFr ? "Aperçu courriels" : "Email Preview",    sub: isFr ? "Tous les templates" : "All Templates",      req: "admin" },
                    { to: "#",                                 label: isFr ? "Copilote Admin"   : "Admin Copilot",    sub: "AI Assistant",                                      req: "admin", action: () => setAdminAssistantOpen(true) },
                    { to: "/administration?tab=branding",      label: isFr ? "Image de marque" : "Design System",    sub: "Branding",                                          req: "admin" },
                    { to: "/administration?tab=notifications", label: "Notifications",                                sub: isFr ? "Mobile & alertes"  : "Mobile & Alerts",    req: "admin" },
                    { to: "/administration?tab=processes",     label: isFr ? "Processus"       : "Processes",        sub: isFr ? "Guide opérationnel" : "Ops Guide",           req: "admin" },
                  ]
                }
              ].map((category) => (
                <Box key={category.title} sx={{ flex: 1 }}>
                  <Typography variant="overline" color={BRAND.textMuted} fontWeight={700} sx={{ mb: 2, display: 'block', letterSpacing: 1 }}>
                    {category.title}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {category.items.filter(item => {
                      if (has("admin")) return true;
                      if (item.req === "finance") return has("finance");
                      if (item.req === "admin") return false;
                      if (item.req === "legal") return has("legal");
                      // "any" = personnel opérationnel (coachs / gestionnaires de comptes),
                      // pas un compte finance-only
                      if (item.req === "any") return has("professor") || has("account manager");
                      return true;
                    }).map(item => (
                      <MegaLink key={item.label} to={item.to} onClick={(e) => {
                        if (item.action) { e.preventDefault(); item.action(); }
                        setAdminOpen(false);
                      }}>
                        <span className="label">{item.label}</span>
                        <span className="sub">{item.sub}</span>
                      </MegaLink>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </MegaMenuOverlay>

          <Dialog
            open={inboxOpen}
            onClose={() => setInboxOpen(false)}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { borderRadius: 3, height: '600px', overflow: 'hidden' } }}
          >
            <Box display="flex" height="100%">
              {/* LEFT: THREAD LIST */}
              <Box width={selectedThreadId ? { xs: 0, md: '35%' } : '100%'} borderRight="1px solid #eee" display={selectedThreadId ? { xs: 'none', md: 'block' } : 'block'} bgcolor="#f8fafc">
                <Box p={2} borderBottom="1px solid #eee" bgcolor="white">
                  <Typography variant="h6" fontWeight={800}>Chats</Typography>
                </Box>
                <List sx={{ p: 0, overflowY: 'auto', height: 'calc(100% - 65px)' }}>
                  {threads.length === 0 && <Box p={3} textAlign="center" color="text.secondary">No conversations</Box>}
                  {threads.map(thread => (
                    <ListItem
                      button
                      key={thread.id}
                      selected={selectedThreadId === thread.id}
                      onClick={() => setSelectedThreadId(thread.id)}
                      sx={{ borderBottom: '1px solid #f1f5f9' }}
                    >
                      <ListItemAvatar>
                        <Badge badgeContent={thread.unreadCount} color="error" overlap="circular" anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                          <Avatar src={thread.avatar} sx={{ bgcolor: BRAND.ellipseGreen }}>{Initials(thread.name)}</Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography fontWeight={thread.unreadCount > 0 ? 800 : 700} noWrap>{thread.name}</Typography>}
                        secondary={<Typography variant="body2" color={thread.unreadCount > 0 ? "text.primary" : "text.secondary"} fontWeight={thread.unreadCount > 0 ? 600 : 400} noWrap>{thread.lastMsg?.content}</Typography>}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* RIGHT: CHAT AREA */}
              <Box flex={1} display={selectedThreadId ? 'flex' : { xs: 'none', md: 'flex' }} flexDirection="column" bgcolor="white">
                {selectedThreadId ? (
                  <>
                    {/* HEADER */}
                    <Box p={2} borderBottom="1px solid #eee" display="flex" alignItems="center" gap={1} bgcolor="white">
                      <IconButton onClick={() => setSelectedThreadId(null)} sx={{ display: { md: 'none' } }}><ArrowBackIcon /></IconButton>
                      <Avatar src={activeThread?.avatar} sx={{ width: 32, height: 32 }}>{Initials(activeThread?.name)}</Avatar>
                      <Typography variant="subtitle1" fontWeight={800}>{activeThread?.name}</Typography>
                      <Box flex={1} />
                      <IconButton onClick={() => handleDeleteThread(activeThread)} color="error" title="Delete Conversation">
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    {/* MESSAGES */}
                    <Box flex={1} p={2} sx={{ overflowY: 'auto', bgcolor: '#f1f5f9' }}>
                      {activeThread?.messages.map((msg, i) => {
                        const isMe = msg.fromId === props.auth.uid;
                        // Mark as read side-effect? Better to do it on load.


                        const handleDeleteMessage = async (msgId) => {
                          if (window.confirm("Delete this message?")) {
                            await firebase.firestore().collection("messages").doc(msgId).delete();
                          }
                        };

                        return (
                          <Box
                            key={msg.id}
                            display="flex"
                            justifyContent={isMe ? 'flex-end' : 'flex-start'}
                            mb={1}
                            sx={{ '&:hover .delete-btn': { opacity: 1 } }}
                          >
                            {isMe && (
                              <IconButton
                                className="delete-btn"
                                size="small"
                                onClick={() => handleDeleteMessage(msg.id)}
                                sx={{ opacity: 0.5, mr: 1, color: 'text.secondary', '&:hover': { opacity: 1, color: '#ef4444' } }}
                              >
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            )}
                            {!isMe && (
                              <Avatar
                                src={activeThread?.avatar}
                                sx={{ width: 32, height: 32, mr: 1 }}
                              >
                                {Initials(activeThread?.name)}
                              </Avatar>
                            )}
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                maxWidth: '70%',
                                borderRadius: 3,
                                bgcolor: isMe ? '#0c498a' : 'white',
                                color: isMe ? 'white' : 'text.primary',
                                borderTopRightRadius: isMe ? 0 : 3,
                                borderTopLeftRadius: !isMe ? 0 : 3
                              }}
                            >
                              <Typography variant="body1">{msg.content}</Typography>
                              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                                {msg.createdAt?.seconds ? new Date(msg.createdAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '...'}
                              </Typography>
                            </Paper>
                          </Box>
                        );
                      })}
                    </Box>

                    {/* INPUT */}
                    <Box p={2} borderTop="1px solid #eee" bgcolor="white" component="form" onSubmit={(e) => { e.preventDefault(); handleReply(); }}>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Type a message..."
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                        />
                        <IconButton color="primary" type="submit" disabled={!replyText.trim()}>
                          <SendIcon />
                        </IconButton>
                      </Stack>
                    </Box>
                  </>
                ) : (
                  <Box flex={1} display="flex" alignItems="center" justifyContent="center" flexDirection="column" color="text.secondary">
                    <MailIcon sx={{ fontSize: 60, opacity: 0.1, mb: 2 }} />
                    <Typography>Select a conversation to start chatting</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Dialog>

          {/* --- SEPARATE PROFILE MENU --- */}
          <Menu
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            PaperProps={{ sx: { borderRadius: 4, minWidth: 200, mt: 1, boxShadow: '0 10px 40px rgba(0,0,0,0.15)' } }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box px={3} py={2} bgcolor={BRAND.mist} display="flex" flexDirection="column" alignItems="center">
              <Box position="relative" mb={1}>
                <Avatar
                  src={currentAvatar}
                  imgProps={{ referrerPolicy: "no-referrer" }}
                  sx={{ width: 64, height: 64, fontSize: 24, bgcolor: 'white', color: BRAND.slate, border: '2px solid white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                >
                  {Initials(profile?.firstname)}
                </Avatar>
                <IconButton size="small" component="label" sx={{ position: 'absolute', bottom: -4, right: -4, bgcolor: 'white', boxShadow: 1, width: 28, height: 28, '&:hover': { bgcolor: '#f5f5f5' } }}>
                  <input hidden accept="image/png, image/jpeg, image/jpg, image/webp" type="file" onChange={handleAvatarChange} />
                  {uploading ? <CircularProgress size={16} /> : <PhotoCameraIcon sx={{ fontSize: 16, color: BRAND.accent }} />}
                </IconButton>
                {currentAvatar && (
                  <IconButton
                    size="small"
                    onClick={handleDeleteAvatar}
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      bgcolor: 'white',
                      boxShadow: 1,
                      width: 24,
                      height: 24,
                      '&:hover': { bgcolor: '#fee2e2' }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                  </IconButton>
                )}
              </Box>
              <Box mb={0.5} onClick={() => { setEditOpen(true); handleCloseUserMenu(); }} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                <Typography fontWeight={800} variant="subtitle1" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                  {profile?.firstname} {profile?.lastname}
                </Typography>
                <EditIcon sx={{ fontSize: 14, ml: 1, color: '#94a3b8' }} />
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-all' }}>{profile?.email}</Typography>
              {process.env.REACT_APP_VERSION && (
                <Typography variant="caption" sx={{ color: '#b0b8c8', fontFamily: 'monospace', fontSize: 9, mt: 0.5, display: 'block' }}>
                  build {process.env.REACT_APP_VERSION} · {process.env.REACT_APP_BUILD_DATE}
                </Typography>
              )}

              {/* EDIT PROFILE BUTTON */}
              <MenuItem onClick={() => { setEditOpen(true); handleCloseUserMenu(); }} sx={{ py: 1, my: 1, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                <EditIcon sx={{ mr: 2, color: BRAND.accent, fontSize: 20 }} />
                <Typography fontWeight={600} variant="body2" color={BRAND.accent}>Edit Profile Details</Typography>
              </MenuItem>

              {/* ROOM CONFIG (Admins Only) */}
              {has("admin") && (
                <Box mt={2} width="100%" bgcolor="white" p={1.5} borderRadius={2} border="1px dashed #ddd">
                  <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block" mb={0.5} fontSize={10}>
                    MY ROOM SLUG (EDITABLE)
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="gray">/</Typography>
                    <TextField
                      variant="standard"
                      fullWidth
                      placeholder="ex: room-martin"
                      defaultValue={profile?.meetingSlug || `room-${(profile?.firstname || 'user').toLowerCase()}`}
                      InputProps={{ disableUnderline: true, style: { fontSize: 14, fontWeight: 700, color: BRAND.accent } }}
                      onBlur={(e) => {
                        let val = e.target.value.trim();
                        val = val.replace(/[^a-zA-Z0-9-_]/g, '-'); // Sanitize
                        e.target.value = val; // Reflect sanitization
                        if (val && val !== profile.meetingSlug) {
                          firebase.firestore().collection("users").doc(auth.uid).update({ meetingSlug: val });
                        }
                      }}
                    />
                    <EditIcon sx={{ fontSize: 14, color: '#ccc' }} />
                  </Box>
                </Box>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={profile?.sharePhoto !== false}
                    onChange={handleTogglePrivacy}
                    size="small"
                    color="primary"
                  />
                }
                label={<Typography variant="caption" color="textSecondary">Public on leaderboards</Typography>}
                sx={{ mt: 1, width: '100%', justifyContent: 'center', ml: 0 }}
              />

            </Box>
            <Divider />

            <MenuItem onClick={() => { history.push("/attendance"); handleCloseUserMenu(); }} sx={{ py: 1 }}>
              <ContactsIcon sx={{ mr: 2, opacity: 0.5, fontSize: 20 }} /> {t("navigation_my_attendance") || "Attendance"}
            </MenuItem>
            {(has("admin") || !has("professor")) && (
              <MenuItem onClick={() => { history.push("/arcade"); handleCloseUserMenu(); }} sx={{ py: 1 }}>
                <SmartToyIcon sx={{ mr: 2, opacity: 0.5, fontSize: 20 }} /> Arcade
              </MenuItem>
            )}
            <Divider sx={{ my: 1 }} />

            {/* Language Switch */}
            <Typography variant="overline" sx={{ px: 2, pt: 1, color: '#aaa', fontWeight: 700, display: 'block' }}>Language</Typography>
            <MenuItem onClick={() => { handleLanguageChange('en'); handleCloseUserMenu(); }}>
              <Typography variant="body2" fontWeight={i18n.language === 'en' ? 700 : 400}>English</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleLanguageChange('fr'); handleCloseUserMenu(); }}>
              <Typography variant="body2" fontWeight={i18n.language === 'fr' ? 700 : 400}>Français</Typography>
            </MenuItem>
            <Divider sx={{ my: 1 }} />



            {has("admin") && (
              <>
                <Typography variant="overline" sx={{ px: 2, color: '#aaa', fontWeight: 700 }}>Debug</Typography>
                <MenuItem onClick={async () => {
                  if (window.confirm("Reset onboarding?")) {
                    await firebase.firestore().collection("users").doc(auth.uid).set({
                      onboardingCompleted: false, // Force False
                      jobTitle: "",               // Wipe
                      Level: "",                  // Wipe
                      level: ""                   // Wipe
                    }, { merge: true });
                    window.location.href = '/onboarding'; // Direct jump
                  }
                }} sx={{ py: 1 }}>
                  <MagicIcon sx={{ mr: 2, opacity: 0.5, fontSize: 20, color: '#ef4444' }} />
                  <Typography color="error">Reset Onboarding</Typography>
                </MenuItem>
              </>
            )}

            <Typography variant="overline" sx={{ px: 2, color: '#aaa', fontWeight: 700 }}>Account</Typography>
            <MenuItem onClick={() => { history.push("/contracts"); handleCloseUserMenu(); }} sx={{ py: 1 }}><FileIcon sx={{ mr: 2, opacity: 0.5, fontSize: 20 }} /> {t("navigation_my_contracts", "My Contracts")}</MenuItem>
            <MenuItem onClick={() => { history.push("/invoices"); handleCloseUserMenu(); }} sx={{ py: 1 }}><FileIcon sx={{ mr: 2, opacity: 0.5, fontSize: 20 }} /> {t("navigation_my_invoices", "My Invoices")}</MenuItem>
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main', py: 1 }}><LogoutIcon sx={{ mr: 2, fontSize: 20 }} /> {t("common.logout", "Log out")}</MenuItem>


          </Menu>

          {/* MORE MENU */}
          <Menu
            anchorEl={moreAnchor}
            open={Boolean(moreAnchor)}
            onClose={() => setMoreAnchor(null)}
            PaperProps={{ sx: { borderRadius: 4, minWidth: 220, mt: 1, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' } }}
          >
            {/* NARRATIVE FLUENT (New AI) */}
            {(has("admin") || !has("professor")) && (
              <MenuItem onClick={() => { history.push("/narrative"); setMoreAnchor(null); }} sx={{ py: 1 }}>
                <MagicIcon sx={{ mr: 2, color: '#8b5cf6' }} />
                <Box>
                  <Typography variant="body2" fontWeight={700}>Narrative Fluent</Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: 10 }}>AI Story Engine</Typography>
                </Box>
              </MenuItem>
            )}

            <Divider sx={{ my: 0.5 }} />

            {/* ACADEMY (Moved) */}
            {canSeeAcademy && (
              <MenuItem onClick={() => { history.push("/academy"); setMoreAnchor(null); }} sx={{ py: 1 }}>
                <SchoolIcon sx={{ mr: 2, color: BRAND.slate }} /> Academy
              </MenuItem>
            )}

            {canSeeResourcesLocal && (
              <MenuItem onClick={() => { history.push("/resources"); setMoreAnchor(null); }} sx={{ py: 1 }}>
                <VideoLibraryIcon sx={{ mr: 2, color: BRAND.slate }} /> {t("navigation_resources") || "Confidence Lab"}
              </MenuItem>
            )}

            <Divider sx={{ my: 0.5 }} />
            {/* PLACES & TOOLS */}
            {has("admin") && (
              <MenuItem onClick={() => {
                const slug = profile.meetingSlug || `room-${(profile.firstname || 'user').toLowerCase()}`;
                history.push(`/room/${slug}?type=pro`);
                setMoreAnchor(null);
              }}
                sx={{ py: 1 }}
              >
                <VideoLibraryIcon sx={{ mr: 2, color: BRAND.ellipseGreen }} /> {i18n.language === 'fr' ? "Ma Salle" : "My Room"}
              </MenuItem>
            )}
            <Divider sx={{ my: 0.5 }} />

            {(has("admin") || has("professor") || has("account manager")) && (
              <>
                <MenuItem onClick={() => { history.push("/userManagement"); setMoreAnchor(null); }} sx={{ py: 1 }}>
                  <ManageAccountsIcon sx={{ mr: 2, color: BRAND.slate }} /> {t('navigation_user_management')}
                </MenuItem>

                <MenuItem onClick={() => { history.push("/sprintattendance"); setMoreAnchor(null); }} sx={{ py: 1 }}>
                  <ContactsIcon sx={{ mr: 2, color: BRAND.accent }} /> Sprint Attendance
                </MenuItem>

                <MenuItem onClick={() => { history.push("/mastermind/coach"); setMoreAnchor(null); }} sx={{ py: 1 }}>
                  <PremiumIcon sx={{ mr: 2, color: '#c9a24b' }} /> {i18n.language === 'fr' ? "Mes cohortes Mastermind" : "My Mastermind cohorts"}
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
            {(() => {
              const canAccessConfidence = has("admin") || has("professor") || profile?.accessToConfidenceWheel === true;
              return (
                <MenuItem
                  onClick={() => {
                    if (canAccessConfidence) {
                      history.push("/confidence");
                      setMoreAnchor(null);
                    } else {
                      setUnlockDialogOpen(true);
                      setMoreAnchor(null);
                    }
                  }}
                  sx={{ py: 1, opacity: canAccessConfidence ? 1 : 0.6 }}
                >
                  <AssessmentIcon sx={{ mr: 2, color: canAccessConfidence ? BRAND.ellipseGreen : 'gray' }} />
                  {t("navigation_my_confidence") || "Wheel"}
                  {!canAccessConfidence && <LockIcon fontSize="small" sx={{ ml: 'auto', color: 'gray' }} />}
                </MenuItem>
              );
            })()}
            {(has("admin") || !has("professor")) && (
              <MenuItem onClick={() => { history.push("/arcade"); setMoreAnchor(null); }} sx={{ py: 1 }}>
                <SmartToyIcon sx={{ mr: 2, color: '#ec4899' }} /> Arcade
              </MenuItem>
            )}
            <MenuItem onClick={() => { history.push("/attendance"); setMoreAnchor(null); }} sx={{ py: 1 }}>
              <ContactsIcon sx={{ mr: 2, color: BRAND.accent }} /> {t("navigation_my_attendance") || "Attendance"}
            </MenuItem>
          </Menu>



          {/* AI Coach - Show only if NOT Admin (Admins have their own tool) */}
          {!has("admin") && (
            <AiCoach
              userId={auth?.uid}
              firstname={profile?.firstname}
              isOpen={coachOpen}
              onClose={() => setCoachOpen(false)}
              zIndex={99999}
            />
          )}

          {/* Final Auto-Evaluation Reminder (2 weeks before program end) */}
          {!has("admin") && (
            <FinalAutoEvaluationModal
              open={finalEvalModalOpen}
              profile={profile}
              auth={auth}
              onClose={() => setFinalEvalModalOpen(false)}
              onComplete={() => setFinalEvalModalOpen(false)}
            />
          )}

          {/* PROFILE EDIT MODAL */}
          <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
            <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Edit Details</DialogTitle>
            <DialogContent>
              <Box pt={2} display="flex" flexDirection="column" gap={2}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="First Name"
                    defaultValue={profile?.firstname}
                    onBlur={(e) => handleUpdateProfile('firstname', e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                  <TextField
                    label="Last Name"
                    defaultValue={profile?.lastname}
                    onBlur={(e) => handleUpdateProfile('lastname', e.target.value)}
                    fullWidth
                    variant="outlined"
                  />
                </Stack>
                <TextField
                  label="City"
                  defaultValue={profile?.AdressCity}
                  onBlur={(e) => firebase.firestore().collection("users").doc(auth.uid).update({ AdressCity: e.target.value })}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Company Name"
                  defaultValue={profile?.companyName}
                  onBlur={(e) => firebase.firestore().collection("users").doc(auth.uid).update({ companyName: e.target.value })}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Job Title"
                  defaultValue={profile?.jobTitle}
                  onBlur={(e) => firebase.firestore().collection("users").doc(auth.uid).update({ jobTitle: e.target.value })}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Phone Number"
                  defaultValue={profile?.phoneNumber}
                  onBlur={(e) => handleUpdateProfile('phoneNumber', e.target.value)}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="LinkedIn"
                  defaultValue={profile?.linkedIn}
                  placeholder="https://linkedin.com/in/..."
                  onBlur={(e) => firebase.firestore().collection("users").doc(auth.uid).update({ linkedIn: e.target.value })}
                  fullWidth
                  variant="outlined"
                />
                {/* Calendly for Professors */}
                {(has('professor') || has('admin')) && (() => {
                  const myProf = props.professors ? Object.values(props.professors).find(p => p && (p.uid === auth.uid || (p.email && p.email === auth.email))) : null;
                  const currentCalendly = myProf?.urlCalendly || myProf?.calendly || profile?.calendly || "";
                  return (
                    <TextField
                      label="Calendly Link"
                      defaultValue={currentCalendly}
                      placeholder="https://calendly.com/..."
                      onBlur={(e) => handleUpdateProfile('urlCalendly', e.target.value)}
                      fullWidth
                      variant="outlined"
                      helperText="For students to book sessions."
                    />
                  );
                })()}
                <TextField
                  label="Short Bio"
                  multiline
                  rows={2}
                  defaultValue={profile?.bio}
                  onBlur={(e) => firebase.firestore().collection("users").doc(auth.uid).update({ bio: e.target.value })}
                  fullWidth
                  variant="outlined"
                />

                <FormControl fullWidth>
                  <InputLabel>English Level (Self-Assessed)</InputLabel>
                  <Select
                    label="English Level (Self-Assessed)"
                    defaultValue={profile?.Level || profile?.level || ""}
                    onChange={(e) => firebase.firestore().collection("users").doc(auth.uid).update({ Level: e.target.value })}
                  >
                    {LEVELS.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                  </Select>
                </FormControl>

                <TextField
                  label="Why perfect English?"
                  multiline
                  rows={2}
                  defaultValue={profile?.reasonForEnglish}
                  onBlur={(e) => firebase.firestore().collection("users").doc(auth.uid).update({ reasonForEnglish: e.target.value })}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="10/10 Program Expectation"
                  multiline
                  rows={2}
                  defaultValue={profile?.programExpectation}
                  onBlur={(e) => firebase.firestore().collection("users").doc(auth.uid).update({ programExpectation: e.target.value })}
                  fullWidth
                  variant="outlined"
                />

                <TextField
                  label="Main Goal (Legacy)"
                  multiline
                  rows={2}
                  defaultValue={profile?.goals}
                  onBlur={(e) => firebase.firestore().collection("users").doc(auth.uid).update({ goals: e.target.value })}
                  fullWidth
                  variant="outlined"
                  helperText="Summary goal from original setup."
                />

                <Divider />
                <Typography variant="overline" color="textSecondary" fontWeight={800}>
                  {t("navigation_notifications") || "Notifications"}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={profile?.weeklyReminderOptOut !== true}
                      onChange={(e, checked) => handleUpdateProfile('weeklyReminderOptOut', !checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {t("navigation_weekly_reminders") || "Weekly Reminders"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Receive a weekly recap of your sprints & hours.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={() => setEditOpen(false)} variant="contained" color="primary" fullWidth size="large">
                Save & Close
              </Button>
            </DialogActions>
          </Dialog>


          {/* ADMIN AI ASSISTANT (Legacy - Commented out to use new Admin Copilot)
          {
            has("admin") && (
              <AdminAiAssistant
                profile={profile}
                isOpen={adminAssistantOpen}
                setIsOpen={setAdminAssistantOpen}
              />
            )
          }
          */}

        </>
      )}

      {/* NEW PORTAL WELCOME */}
      <Dialog
        open={newPortalOpen}
        onClose={handleDismissNewPortal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: '#0f172a',
            border: '1px solid rgba(146,209,220,0.15)',
            boxShadow: '0 40px 100px rgba(0,0,0,0.6), 0 0 0 1px rgba(146,209,220,0.08)',
            overflow: 'hidden',
            m: 2,
          }
        }}
        BackdropProps={{ sx: { backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.7)' } }}
      >
        {/* Hero section */}
        <Box sx={{
          position: 'relative',
          pt: 4, pb: 3, px: 4,
          textAlign: 'center',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, rgba(146,209,220,0.08) 0%, transparent 100%)',
        }}>
          {/* Glow blob */}
          <Box sx={{
            position: 'absolute', top: -40, left: '50%', transform: 'translateX(-50%)',
            width: 200, height: 200, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(146,209,220,0.18) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          {/* Language switcher — top right */}
          <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 0.5 }}>
            {['fr', 'en'].map((lng) => (
              <Box
                key={lng}
                onClick={() => handleLanguageChange(lng)}
                sx={{
                  px: 1.2, py: 0.3, borderRadius: 99, cursor: 'pointer',
                  fontSize: '0.68rem', fontWeight: 700, letterSpacing: 1,
                  textTransform: 'uppercase',
                  color: i18n.language === lng ? '#0f172a' : 'rgba(255,255,255,0.3)',
                  background: i18n.language === lng ? '#92d1dc' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${i18n.language === lng ? 'transparent' : 'rgba(255,255,255,0.1)'}`,
                  transition: 'all 0.15s',
                  '&:hover': { color: i18n.language === lng ? '#0f172a' : 'rgba(255,255,255,0.6)' },
                }}
              >
                {lng}
              </Box>
            ))}
          </Box>

          {/* Logo */}
          <Box sx={{ mb: 2.5 }}>
            <img src={logo} alt="Ellipse" style={{ height: 32, opacity: 0.95 }} />
          </Box>

          {/* Badge */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.8,
            px: 2, py: 0.6, borderRadius: 99, mb: 2,
            background: 'rgba(146,209,220,0.12)',
            border: '1px solid rgba(146,209,220,0.25)',
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#92d1dc', boxShadow: '0 0 8px #92d1dc' }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 1.5, color: '#92d1dc', textTransform: 'uppercase' }}>
              {isFr ? 'Nouveau · 22 mai 2025' : 'New · May 22, 2025'}
            </Typography>
          </Box>

          <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#fff', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            {isFr ? 'Ton portail.\nReinventé.' : 'Your portal.\nReimagined.'}
          </Typography>
          <Typography sx={{ mt: 1, fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
            {isFr ? 'Conçu autour de toi. Pas l\'inverse.' : 'Built around you. Not the other way around.'}
          </Typography>
          <Box sx={{
            mt: 1.5, px: 1.5, py: 0.6, borderRadius: 99,
            background: 'rgba(191,213,108,0.12)',
            border: '1px solid rgba(191,213,108,0.25)',
            display: 'inline-block',
          }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#bfd56c', fontWeight: 600 }}>
              {isFr ? '👋 Prochaine étape : ton onboarding' : '👋 Next step: your onboarding'}
            </Typography>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(146,209,220,0.2), transparent)', mx: 0 }} />

        {/* Features list */}
        <Box sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[
            {
              icon: '✨',
              color: '#92d1dc',
              title: isFr ? 'Un design taillé pour toi' : 'Designed to feel different',
              desc: isFr ? 'Interface glassmorphique, couleurs repensées. Fluide, élégant, pensé jusqu\'au dernier pixel.' : 'Glassmorphic interface, refreshed palette. Fluid, elegant, crafted to the last pixel.',
            },
            {
              icon: '👤',
              color: '#bfd56c',
              title: isFr ? 'Onboarding personnalisé' : 'Personalized onboarding',
              desc: isFr ? 'On apprend à te connaître pour adapter ton expérience à tes objectifs.' : 'We learn about you to tailor your experience to your goals.',
            },
            {
              icon: '📅',
              color: '#f5d377',
              title: isFr ? 'Gestion des séances simplifiée' : 'Simplified session management',
              desc: isFr ? 'Annule, replanifie et configure des rappels directement depuis le portail.' : 'Cancel, reschedule, and set reminders directly from the portal.',
            },
            {
              icon: '📱',
              color: '#92d1dc',
              title: isFr ? 'App mobile — bientôt disponible' : 'Mobile app — coming soon',
              desc: isFr ? 'L\'application Ellipse arrive sur iOS et Android. Reste à l\'affût !' : 'The Ellipse app is coming to iOS and Android. Stay tuned!',
            },
          ].map((item, i, arr) => (
            <Box key={item.title} sx={{
              display: 'flex', gap: 2, alignItems: 'flex-start',
              py: 1.8,
              borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
            }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                background: `${item.color}18`,
                border: `1px solid ${item.color}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 17,
              }}>
                {item.icon}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff', lineHeight: 1.3 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', mt: 0.2, lineHeight: 1.4 }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* CTA */}
        <Box sx={{ px: 3, pb: 3, pt: 0.5 }}>
          <Button
            onClick={handleDismissNewPortal}
            fullWidth
            sx={{
              borderRadius: 99, py: 1.5,
              background: 'linear-gradient(90deg, #92d1dc, #7ec4d0)',
              color: '#0f172a', fontWeight: 800, fontSize: '0.95rem',
              boxShadow: '0 4px 24px rgba(146,209,220,0.25)',
              textTransform: 'none',
              '&:hover': { boxShadow: '0 8px 32px rgba(146,209,220,0.4)', filter: 'brightness(1.06)' },
            }}
          >
            {isFr ? 'Commencer →' : 'Get started →'}
          </Button>
          <Typography sx={{ mt: 2, textAlign: 'center', fontSize: '0.73rem', color: 'rgba(255,255,255,0.25)', lineHeight: 1.5 }}>
            {isFr ? 'Une question ? On est là.' : 'Questions? We\'re here.'}{' '}
            <Box component="a" href="mailto:hello@groupellipse.com" sx={{ color: 'rgba(146,209,220,0.6)', textDecoration: 'none', '&:hover': { color: '#92d1dc' } }}>
              hello@groupellipse.com
            </Box>
          </Typography>
        </Box>
      </Dialog>

      {/* UNLOCK DIALOG */}
      <Dialog open={unlockDialogOpen} onClose={() => setUnlockDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 800 }}>Feature Locked 🔒</DialogTitle>
        <DialogContent>
          <Typography>Unlock your Confidence Wheel assessment now to access this feature!</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUnlockDialogOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            sx={{ bgcolor: BRAND.ellipseGreen, color: 'black', fontWeight: 700 }}
            onClick={() => {
              history.push("/boutique?tab=course");
              setUnlockDialogOpen(false);
            }}
          >
            Unlock Now
          </Button>
        </DialogActions>
      </Dialog>

    </ Root>
  );
}

// --- Redux Connection ---
const mapState = (state) => ({
  auth: state.firebase.auth,
  profile: state.firebase.profile,
  receivedMessages: state.firestore.ordered.receivedMessages,
  sentMessages: state.firestore.ordered.sentMessages,
  professors: state.firestore.ordered.professor || state.firestore.ordered.myProfessorProfile,
  threadUsers: state.firestore.data.threadUsers // Data structure (object) for fast lookup by ID
});

export default compose(
  connect(mapState),
  firestoreConnect((props) => {
    if (!props.auth || !props.auth.uid) return [];

    const queries = [
      {
        collection: 'messages',
        where: ['toId', '==', props.auth.uid],
        limit: 500,
        storeAs: 'receivedMessages'
      },
      {
        collection: 'messages',
        where: ['fromId', '==', props.auth.uid],
        limit: 500,
        storeAs: 'sentMessages'
      },
      // Target only MY professor profile if I am a coach/staff
      {
        collection: 'professor',
        where: [['email', '==', props.auth.email]],
        storeAs: 'myProfessorProfile'
      }
    ];

    return queries;
  })
)(Navigation);
